/**
 * React Query hooks for article section generation
 *
 * This file provides type-safe React Query mutation hooks for article
 * section generation with automatic retry logic and SSE streaming support.
 */

import { useMutation } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';

// Get API URL from environment or use default
import { API_BASE_URL as API_URL } from '../../../config/api.js';

/**
 * Section generation request parameters
 */
interface GenerateSectionParams {
  articleId: string;
  sectionIndex: number;
  styleGuide?: string;
  prompt?: string;
  onProgress?: (chunk: string) => void;
  onComplete?: (content: string) => void;
  signal?: AbortSignal;
}

/**
 * Section generation result
 */
interface GenerateSectionResult {
  content: string;
  sectionIndex: number;
}

/**
 * Build request headers with authentication token
 */
async function buildAuthHeaders(getToken: () => Promise<string | null>): Promise<HeadersInit> {
  const token = await getToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

/**
 * Handle error response and extract error message
 */
async function handleErrorResponse(response: Response): Promise<never> {
  let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

  try {
    const errorData = await response.json();
    if (errorData.error) {
      errorMessage = errorData.error;
    }
  } catch (parseError) {
    // JSON parsing failed - response body is not JSON or already consumed
    console.warn('[GENERATE MUTATION] Failed to parse error response as JSON:', parseError);
  }

  console.error('❌ [GENERATE MUTATION] Request failed', {
    status: response.status,
    error: errorMessage
  });
  throw new Error(errorMessage);
}

/**
 * Parse SSE line and extract event data
 */
function parseSSELine(line: string): { type: string; chunk?: string; error?: string } | null {
  if (!line.startsWith('data: ')) {
    return null;
  }

  const data = line.substring(6).trim();

  // Skip empty data or comments
  if (!data || data.startsWith(':')) {
    return null;
  }

  try {
    return JSON.parse(data);
  } catch (parseError) {
    // Skip invalid JSON lines (may be SSE comments or partial data)
    console.debug('[GENERATE MUTATION] Skipping invalid JSON:', data);
    return null;
  }
}

/**
 * Process SSE stream and handle events
 */
async function processSSEStream(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  sectionIndex: number,
  onProgress?: (chunk: string) => void,
  onComplete?: (content: string) => void
): Promise<GenerateSectionResult> {
  const decoder = new TextDecoder();
  let fullContent = '';

  try {
    for (;;) {
      const { done, value } = await reader.read();

      if (done) {
        console.log('📡 [GENERATE MUTATION] Stream ended');
        break;
      }

      const chunk = decoder.decode(value, { stream: true });
      const lines = chunk.split('\n');

      for (const line of lines) {
        const event = parseSSELine(line);

        if (!event) continue;

        if (event.type === 'content_chunk' && event.chunk) {
          fullContent += event.chunk;
          onProgress?.(event.chunk);
        } else if (event.type === 'complete') {
          console.log('✅ [GENERATE MUTATION] Generation complete', {
            contentLength: fullContent.length
          });
          onComplete?.(fullContent);
          return { content: fullContent, sectionIndex };
        } else if (event.type === 'error') {
          throw new Error(event.error || 'Generation failed');
        }
      }
    }

    // If we reach here, stream ended without 'complete' event
    console.log('⚠️ [GENERATE MUTATION] Stream ended without complete event');
    onComplete?.(fullContent);
    return { content: fullContent, sectionIndex };
  } finally {
    reader.releaseLock();
  }
}

/**
 * Generate article section with SSE streaming
 *
 * This mutation handles Server-Sent Events (SSE) streaming for real-time
 * content generation with automatic retry logic for transient failures.
 *
 * Features:
 * - Automatic retry with exponential backoff (3 attempts: 1s, 2s, 4s)
 * - Handles 404 errors from race conditions (article not yet queryable)
 * - SSE streaming support with progress callbacks
 * - Proper error handling and cleanup
 *
 * @returns React Query mutation for section generation
 *
 * @example
 * ```typescript
 * const generateSection = useGenerateSection();
 *
 * generateSection.mutate({
 *   articleId: 'abc123',
 *   sectionIndex: 0,
 *   styleGuide: 'linkedin',
 *   onProgress: (chunk) => console.log('Received:', chunk),
 *   onComplete: (content) => console.log('Done:', content)
 * }, {
 *   onError: (error) => console.error('Failed:', error),
 *   onSuccess: (result) => console.log('Success:', result)
 * });
 * ```
 */
export function useGenerateSection() {
  const { getToken } = useAuth();

  return useMutation<GenerateSectionResult, Error, GenerateSectionParams>({
    mutationFn: async ({
      articleId,
      sectionIndex,
      styleGuide,
      prompt,
      onProgress,
      onComplete,
      signal
    }) => {
      const url = `${API_URL}/api/articles/${articleId}/sections/${sectionIndex}/generate`;

      console.log('🚀 [GENERATE MUTATION] Starting section generation', {
        articleId,
        sectionIndex,
        styleGuide,
        hasPrompt: !!prompt
      });

      // Build headers with authentication
      const headers = await buildAuthHeaders(getToken);

      // Send request
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          styleGuide,
          prompt
        }),
        signal
      });

      // Handle error responses
      if (!response.ok) {
        await handleErrorResponse(response);
      }

      console.log('✅ [GENERATE MUTATION] Response received, starting SSE stream');

      // Process SSE streaming
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('Response body is not readable');
      }

      return await processSSEStream(reader, sectionIndex, onProgress, onComplete);
    },

    // Automatic retry with exponential backoff
    // Handles race conditions where article isn't immediately queryable
    retry: 3,
    retryDelay: (attemptIndex) => {
      const delay = Math.min(1000 * 2 ** attemptIndex, 5000);
      console.log(`🔄 [GENERATE MUTATION] Retry attempt ${attemptIndex + 1} in ${delay}ms`);
      return delay;
    },

    onError: (error) => {
      console.error('❌ [GENERATE MUTATION] Final error after retries:', error.message);
    }
  });
}
